import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Copy, Download, Sparkles, CheckCircle, RefreshCw, Rocket, Search, Users, MessageSquare, Pencil, Save, X } from 'lucide-react'
import { engineApiClient } from '@/lib/apiClient'
import { trackEvent } from '@/lib/monitoring'
import type { OutputDrafts } from '@/lib/contracts'
import { OutputRenderer } from '@/components/common/OutputRenderer'
import { ScorecardUI } from '@/components/common/ScorecardUI'
import { exportSectionToWord, exportAllToWord } from '@/lib/exportToWord'

const FALLBACK_PROJECT_ID = 'alpha-grid'

type DraftKey = keyof OutputDrafts

const TABS: { id: DraftKey; label: string }[] = [
  { id: 'jd', label: 'Full JD' },
  { id: 'brief', label: 'Search Brief' },
  { id: 'interviewGuide', label: 'Interview Guide' },
  { id: 'scorecard', label: 'Scorecard' },
  { id: 'summary', label: 'Executive Summary' },
  { id: 'outreach', label: 'Outreach Brief' },
]

export const OutputGeneration = () => {
  const { id } = useParams<{ id: string }>()
  const projectId = id ?? FALLBACK_PROJECT_ID
  const navigate = useNavigate()

  const [drafts, setDrafts] = useState<OutputDrafts | null>(null)
  const [version, setVersion] = useState<number>(0)
  const [activeTab, setActiveTab] = useState<DraftKey>('jd')
  const [isGenerating, setIsGenerating] = useState(false)
  const [autoTriggered, setAutoTriggered] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const [mustHaves, setMustHaves] = useState<string[]>([])
  const [leadershipSignals, setLeadershipSignals] = useState<string[]>([])
  const [niceToHaves, setNiceToHaves] = useState<string[]>([])
  const [alreadyLaunched, setAlreadyLaunched] = useState(false)

  useEffect(() => {
    // Check if search has already been launched (candidates exist)
    engineApiClient.getSearchStatus(projectId)
      .then((res) => { if (res.status === 'complete') setAlreadyLaunched(true) })
      .catch(() => {})
    engineApiClient.listCandidates(projectId)
      .then((res) => { if (res.candidates && res.candidates.length > 0) setAlreadyLaunched(true) })
      .catch(() => {})
    engineApiClient.getOutputs(projectId)
      .then((res) => {
        const hasContent = Object.values(res.drafts).some((v) => v && v.length > 20)
        if (hasContent) {
          setDrafts(res.drafts)
          setVersion(res.version)
        }
      })
      .catch(() => {})

    // Load intake highlights for scorecard criteria
    engineApiClient.getIntakeThread(projectId)
      .then((thread) => {
        setMustHaves(thread.highlights.mustHaves)
        setLeadershipSignals(thread.highlights.leadershipSignals)
        setNiceToHaves(thread.highlights.niceToHaves)
      })
      .catch(() => {})
  }, [projectId])

  const handleGenerate = useCallback(async () => {
    setIsGenerating(true)
    setError(null)
    try {
      const res = await engineApiClient.generateOutputs(projectId)
      setDrafts(res.drafts)
      setVersion(res.version)
      trackEvent('outputs_generated', { projectId })
      engineApiClient.advanceProjectStage(projectId, 'dashboard').catch(() => {})
    } catch {
      setError('Output generation failed. Please try again.')
    } finally {
      setIsGenerating(false)
    }
  }, [projectId])

  // Auto-trigger generation when page loads and no drafts exist
  useEffect(() => {
    if (!autoTriggered && !drafts && !isGenerating) {
      setAutoTriggered(true)
      handleGenerate()
    }
  }, [autoTriggered, drafts, isGenerating, handleGenerate])

  const stripMarkdown = (text: string): string => {
    if (!text) return ''
    return text
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/^[\u2500-\u257F\u2580-\u259F\u2501=]{2,}$/gm, '')
      .replace(/^━+$/gm, '')
      .replace(/^---+$/gm, '')
      .replace(/^___+$/gm, '')
      .replace(/^===+$/gm, '')
      .replace(/^\s*[-*+]\s+/gm, '• ')
      .replace(/\s*—\s*/g, ' ')
      .replace(/\s*–\s*/g, ' ')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  }

  const handleCopy = () => {
    if (drafts) {
      navigator.clipboard.writeText(stripMarkdown(drafts[activeTab])).catch(() => {})
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }

  const handleExportSection = async () => {
    if (!drafts) return
    const label = TABS.find(t => t.id === activeTab)?.label ?? activeTab
    const content = drafts[activeTab] ?? ''
    if (!content.trim()) return
    await exportSectionToWord(label, content)
  }

  const handleExportAll = async () => {
    if (!drafts) return
    const sections = TABS
      .map(t => ({ label: t.label, content: drafts[t.id] ?? '' }))
      .filter(s => s.content.trim().length > 10)
    if (!sections.length) { alert('No outputs generated yet.'); return }
    await exportAllToWord(sections)
  }

  const activeLabel = TABS.find(s => s.id === activeTab)?.label ?? ''

  return (
    <div className="bg-[#02182B] min-h-screen">
      {/* Page header */}
      <div className="border-b border-[#233D4C]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <h1 className="text-2xl font-bold text-white">Outputs</h1>
          <p className="text-gray-400 text-sm mt-1">Your AI-generated recruiter deliverables</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">

        {/* Generating state — waveform loading */}
        {!drafts && (
          <div className="flex flex-col items-center justify-center min-h-[calc(100vh-160px)] space-y-6">
            <style>{`
              @keyframes out-bar-1 { 0%,100%{transform:scaleY(0.25)} 50%{transform:scaleY(1)} }
              @keyframes out-bar-2 { 0%,100%{transform:scaleY(0.3)} 50%{transform:scaleY(1)} }
              @keyframes out-bar-3 { 0%,100%{transform:scaleY(0.2)} 50%{transform:scaleY(1)} }
              @keyframes out-bar-4 { 0%,100%{transform:scaleY(0.3)} 50%{transform:scaleY(1)} }
              @keyframes out-bar-5 { 0%,100%{transform:scaleY(0.25)} 50%{transform:scaleY(1)} }
            `}</style>
            {/* Fixed-height bar container so nothing outside moves */}
            <div className="flex items-center justify-center gap-[5px]" style={{ height: '52px' }}>
              {[
                { kf: 'out-bar-1', dur: '0.8s', delay: '0s' },
                { kf: 'out-bar-2', dur: '0.6s', delay: '0.1s' },
                { kf: 'out-bar-3', dur: '0.7s', delay: '0.05s' },
                { kf: 'out-bar-4', dur: '0.9s', delay: '0.15s' },
                { kf: 'out-bar-5', dur: '0.75s', delay: '0.08s' },
              ].map((b, i) => (
                <div key={i} className="w-[6px] rounded-full bg-[#FD802E]"
                  style={{ height: '52px', animationName: b.kf, animationDuration: b.dur, animationTimingFunction: 'ease-in-out', animationIterationCount: 'infinite', animationDelay: b.delay, transformOrigin: 'center' }} />
              ))}
            </div>
            <div className="text-center" style={{ minHeight: '52px' }}>
              <p className="text-white font-semibold text-lg">Erica is generating your outputs…</p>
              <p className="text-gray-400 text-sm mt-1">Building your JD, search brief, interview guide, scorecard and outreach brief</p>
            </div>
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-400 text-center max-w-md">
                {error}
                <button onClick={handleGenerate} className="block mt-2 text-[#FD802E] underline text-xs">Try again</button>
              </div>
            )}
          </div>
        )}

        {/* Outputs viewer */}
        {drafts && (
          <div className="space-y-6">
            {/* Launch Search CTA — top of page */}
            <div className="bg-[#233D4C] border border-[#FD802E]/30 rounded-xl p-8">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-[#FD802E]/15 rounded-lg flex items-center justify-center">
                      <Rocket size={16} className="text-[#FD802E]" />
                    </div>
                    <span className="text-xs uppercase text-[#FD802E] font-semibold tracking-wide">Next Step</span>
                  </div>
                  <h2 className="text-xl font-bold text-white mb-4">Launch Search</h2>
                  <ul className="space-y-2.5">
                    <li className="flex items-start gap-2.5 text-sm text-gray-300">
                      <Search size={14} className="text-[#FD802E] mt-0.5 shrink-0" />
                      Erica will now search over 250 million profiles
                    </li>
                    <li className="flex items-start gap-2.5 text-sm text-gray-300">
                      <Users size={14} className="text-[#FD802E] mt-0.5 shrink-0" />
                      Erica highlights and ranks the top 50 profiles, adding them into your project
                    </li>
                    <li className="flex items-start gap-2.5 text-sm text-gray-300">
                      <MessageSquare size={14} className="text-[#FD802E] mt-0.5 shrink-0" />
                      Erica then reaches out and conducts a pre-screen interview on selected profiles
                    </li>
                  </ul>
                </div>
                <div className="shrink-0">
                  <button
                    onClick={() => { if (!alreadyLaunched) trackEvent('search_launched', { projectId }); navigate(`/project/${projectId}/dashboard`) }}
                    className="flex items-center gap-2.5 bg-[#FD802E] text-[#233D4C] font-bold px-8 py-4 rounded-xl hover:bg-[#ff8f45] transition-colors text-base shadow-lg shadow-[#FD802E]/20"
                  >
                    <Rocket size={18} />
                    {alreadyLaunched ? 'Return to Project' : 'Launch Search'}
                  </button>
                </div>
              </div>
            </div>

            {/* Version badge + Export All */}
            <div className="flex items-center justify-between">
              <span className="px-2 py-0.5 bg-green-500/10 text-green-400 rounded-full text-xs font-medium flex items-center gap-1">
                <CheckCircle size={12} /> Version {version} — all outputs ready
              </span>
              <button
                onClick={handleExportAll}
                className="flex items-center gap-2 bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-lg hover:border-[#FD802E]/40 hover:text-[#FD802E] transition-colors"
              >
                <Download size={14} /> Export All Outputs
              </button>
            </div>

            {/* Main output card */}
            <div className="bg-[#233D4C] border border-[#2a4a5c] rounded-xl overflow-hidden">
              {/* Tab bar */}
              <div className="border-b border-[#2a4a5c]">
                <div className="flex overflow-x-auto">
                  {TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => { setActiveTab(tab.id); setIsEditing(false) }}
                      className={`px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                        activeTab === tab.id
                          ? 'border-[#FD802E] text-[#FD802E]'
                          : 'border-transparent text-gray-400 hover:text-white'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Content + actions */}
              {activeTab === 'scorecard' ? (
                /* ── Scorecard: full-width interactive UI ── */
                <div className="p-6">
                  <ScorecardUI
                    projectId={projectId}
                    mustHaves={mustHaves}
                    leadershipSignals={leadershipSignals}
                    niceToHaves={niceToHaves}
                  />
                </div>
              ) : (
                /* ── All other tabs: document renderer + actions panel ── */
                <div className="flex flex-col lg:flex-row">
                  <div className="flex-1 min-w-0 p-8 min-h-[520px]">
                    {isEditing ? (
                      <textarea
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-full bg-[#02182B] text-white border border-[#2a4a5c] rounded-lg p-4 text-[15px] leading-relaxed resize-none focus:outline-none focus:border-[#FD802E]/60"
                        style={{ minHeight: '520px' }}
                      />
                    ) : (
                      <OutputRenderer content={drafts[activeTab]} />
                    )}
                  </div>

                  <div className="lg:w-52 lg:flex-shrink-0 border-t lg:border-t-0 lg:border-l border-[#2a4a5c] p-5 space-y-2">
                    <p className="text-xs uppercase text-gray-500 font-medium mb-3">Actions</p>

                    <button
                      onClick={handleCopy}
                      className="w-full flex items-center gap-2 bg-[#FD802E] text-[#233D4C] font-semibold text-sm px-3 py-2.5 rounded-lg hover:bg-[#ff8f45] transition-colors"
                    >
                      <Copy size={13} />
                      {copied ? 'Copied!' : 'Copy'}
                    </button>

                    {!isEditing && (
                      <button
                        onClick={() => { setEditValue(stripMarkdown(drafts[activeTab])); setIsEditing(true) }}
                        className="w-full flex items-center gap-2 bg-[#02182B] border border-[#2a4a5c] text-white text-sm px-3 py-2.5 rounded-lg hover:border-[#FD802E]/40 hover:text-[#FD802E] transition-colors"
                      >
                        <Pencil size={13} /> Edit
                      </button>
                    )}

                    {isEditing && (
                      <>
                        <button
                          onClick={() => { setDrafts(prev => prev ? { ...prev, [activeTab]: editValue } : prev); setIsEditing(false) }}
                          className="w-full flex items-center gap-2 bg-green-600 text-white text-sm px-3 py-2.5 rounded-lg hover:bg-green-500 transition-colors"
                        >
                          <Save size={13} /> Save
                        </button>
                        <button
                          onClick={() => setIsEditing(false)}
                          className="w-full flex items-center gap-2 bg-[#02182B] border border-[#2a4a5c] text-gray-400 text-sm px-3 py-2.5 rounded-lg hover:border-red-500/40 hover:text-red-400 transition-colors"
                        >
                          <X size={13} /> Cancel
                        </button>
                      </>
                    )}

                    <button
                      onClick={handleExportSection}
                      className="w-full flex items-center gap-2 bg-[#02182B] border border-[#2a4a5c] text-white text-sm px-3 py-2.5 rounded-lg hover:border-[#FD802E]/40 hover:text-[#FD802E] transition-colors"
                    >
                      <Download size={13} /> Export to Word
                    </button>

                    <button
                      onClick={handleExportAll}
                      className="w-full flex items-center gap-2 bg-[#02182B] border border-[#2a4a5c] text-white text-sm px-3 py-2.5 rounded-lg hover:border-[#FD802E]/40 hover:text-[#FD802E] transition-colors"
                    >
                      <Download size={13} /> Export All
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  )
}
